import { useMemo } from 'react'
import { isActivityConfirmed } from '../../../pure-js/libs/EnrollmentHelper'
import { generateQRCodesForEnrollment } from '../../../pure-js/libs/QRHelper'
import { Activity, EnrollmentWithPayment } from '../../../pure-js/types/GrooverTypes'
import { State } from './useAppState'
import { isGetObfuscatedEnrollmentResponse, useEnrollment } from '../libs/ReactQueryHooks'

export type RegistrationQrCodeItem = {
  qrCode: string
  activityId: string
  activityName: string
  ticketIndex: number
  isMultipleTickets: boolean
}

export const flattenRegistrationQrCodes = (
  qrCodesByActivity: { activityId: string; qrCodes: string[] }[],
  approvedActivities: Activity[]
): RegistrationQrCodeItem[] => {
  const qrCodes: RegistrationQrCodeItem[] = []

  qrCodesByActivity.forEach((activityQR) => {
    const activity = approvedActivities.find((a) => a.id === activityQR.activityId)
    const isMultipleTickets = activityQR.qrCodes.length > 1

    activityQR.qrCodes.forEach((qrCode, index) => {
      qrCodes.push({
        qrCode,
        activityId: activityQR.activityId,
        activityName: activity?.name || 'Activity',
        ticketIndex: index + 1,
        isMultipleTickets
      })
    })
  })

  return qrCodes
}

type UseRegistrationQrCodesOptions = {
  state: State
  enrollmentUserId?: string
  /**
   * When provided, the internal useEnrollment query is disabled and this data is used directly.
   * Use this in callers that already have the obfuscated enrollment (e.g. RegisterPage) to avoid
   * issuing a second network request with a different React Query cache key.
   */
  preloadedEnrollmentData?: EnrollmentWithPayment | null
}

export const useRegistrationQrCodes = ({
  state,
  enrollmentUserId,
  preloadedEnrollmentData
}: UseRegistrationQrCodesOptions) => {
  // When the caller supplies pre-fetched enrollment data, disable the internal query by zeroing out
  // enrollmentId so the useEnrollment `enabled: !!state.enrollmentId` guard stays false.
  const queryState = useMemo(
    () => (preloadedEnrollmentData != null ? { ...state, enrollmentId: '' } : state),
    [state, preloadedEnrollmentData]
  )
  const { data: enrollmentResponse } = useEnrollment(queryState, enrollmentUserId)
  const enrollment = isGetObfuscatedEnrollmentResponse(enrollmentResponse) ? enrollmentResponse.enrollment : undefined

  const enrollmentData =
    preloadedEnrollmentData ?? (state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment)

  const approvedActivities = useMemo((): Activity[] => {
    if (!enrollmentData?.id) return state.selectedActivities
    const sourceActivities = enrollmentData.activities || state.selectedActivities
    return sourceActivities.filter((a) => isActivityConfirmed(enrollmentData, a.id))
  }, [enrollmentData, state.selectedActivities])

  const qrCodesByActivity = useMemo(() => {
    if (!enrollmentData?.id) return []

    return generateQRCodesForEnrollment({
      id: enrollmentData.id,
      activities: approvedActivities,
      activityQuantities: enrollmentData.activityQuantities
    })
  }, [enrollmentData, approvedActivities])

  const allQRCodes = useMemo(
    (): RegistrationQrCodeItem[] => flattenRegistrationQrCodes(qrCodesByActivity, approvedActivities),
    [qrCodesByActivity, approvedActivities]
  )

  const currentQRData = allQRCodes[0] ?? null

  return {
    enrollment,
    enrollmentData,
    approvedActivities,
    allQRCodes,
    currentQRData
  }
}
